import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { dontShowMobileBannerKey } from '@/app/config/localStorageKeys'
import { type MobileAppVariant } from '@/app/config/mobileAppContent'
import { configService } from '@/app/services/configService'

const SWIPE_THRESHOLD = 60
const TAP_TOLERANCE = 8

interface UseMobileAppBannerControllerParams {
  variant: MobileAppVariant
  onOpenModal: () => void
}

export const useMobileAppBannerController = ({
  variant,
  onOpenModal,
}: UseMobileAppBannerControllerParams) => {
  const { data: configsApp } = useQuery({
    queryKey: ['configs'],
    queryFn: configService.getConfigsApp,
  })

  const appConfig =
    variant === 'admin' ? configsApp?.admin_app : configsApp?.generic_app
  const isBannerActive = appConfig?.is_active === true

  const [shouldShow, setShouldShow] = useState(false)
  const [drag, setDrag] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)
  const startRef = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    const isHidden = localStorage.getItem(dontShowMobileBannerKey(variant))
    // Mobile abre a loja pelo link direto; desktop abre pelo QR Code do modal.
    // Ambas as variantes têm fluxo válido nos dois casos, então basta o gate de is_active.
    setShouldShow(!isHidden)
  }, [variant])

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    startRef.current = { x: e.clientX, y: e.clientY }
    setIsDragging(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!startRef.current) return
    const dx = e.clientX - startRef.current.x
    const dy = e.clientY - startRef.current.y
    setDrag({
      x: dx,
      y: dy < 0 ? dy / 3 : dy,
    })
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!startRef.current) return
    const dx = e.clientX - startRef.current.x
    const dy = e.clientY - startRef.current.y
    startRef.current = null
    setIsDragging(false)

    const absX = Math.abs(dx)
    const absY = Math.abs(dy)

    if (absX < TAP_TOLERANCE && absY < TAP_TOLERANCE) {
      setDrag({ x: 0, y: 0 })
      onOpenModal()
      return
    }

    if (absY > absX) {
      if (dy < -SWIPE_THRESHOLD) {
        setDrag({ x: 0, y: 0 })
        onOpenModal()
        return
      }
      if (dy > SWIPE_THRESHOLD) {
        setDrag({ x: 0, y: window.innerHeight })
        window.setTimeout(() => setIsDismissed(true), 200)
        return
      }
    } else if (absX > SWIPE_THRESHOLD) {
      setDrag({
        x: dx > 0 ? window.innerWidth : -window.innerWidth,
        y: 0,
      })
      window.setTimeout(() => setIsDismissed(true), 200)
      return
    }

    setDrag({ x: 0, y: 0 })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onOpenModal()
    }
  }

  const distance = Math.hypot(drag.x, drag.y)
  const opacity = Math.max(0, 1 - distance / 300)
  const isVisible = shouldShow && !isDismissed && isBannerActive

  return {
    isVisible,
    drag,
    isDragging,
    opacity,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleKeyDown,
  }
}
